// src/data/preferencesClient.js
//
// Thin client for /api/preferences/:key.
// Personal UI state only — not portfolio data.
// All callers must use these two functions; never call /api/preferences
// directly from components.

import { AuthError, NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

const BASE = "/api/preferences";

/**
 * Fetch a stored preference value for the current user.
 *
 * @param {string} key  - The preference key (e.g. "reports_order").
 * @returns {Promise<*>} The stored JSON value, or `null` if no row exists.
 * @throws {NetworkError | AuthError}
 */
export async function getPreference(key) {
  let res;
  try {
    res = await fetch(`${BASE}/${encodeURIComponent(key)}`, {
      method: "GET",
      credentials: "include",
    });
  } catch (err) {
    log.error("preferencesClient: network failure on get", { key, err: String(err) });
    throw new NetworkError(`Failed to reach ${BASE}/${key}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("preferencesClient: non-2xx on get", { key, status: res.status, name: e.name });
    throw e;
  }
  const { value } = await res.json();
  return value;
}

/**
 * Persist a preference value for the current user.
 *
 * @param {string} key   - The preference key (e.g. "reports_order").
 * @param {*}      value - Any JSON-serialisable value.
 * @returns {Promise<void>}
 * @throws {NetworkError | AuthError}
 */
export async function setPreference(key, value, { keepalive = false } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}/${encodeURIComponent(key)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch (err) {
    log.error("preferencesClient: network failure on set", { key, err: String(err) });
    throw new NetworkError(`Failed to reach ${BASE}/${key}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("preferencesClient: non-2xx on set", { key, status: res.status, name: e.name });
    throw e;
  }
}
